"use client";

import { useState, useEffect } from "react";
import { ReactComponent as WithdrawSuccess } from "../../../Assets/Icons/withdrawSuccess.svg";
import PrimaryOutlineButton from "../../common/PrimaryOutlineButton";
import PrimaryButton from "../../common/PrimaryButton";
import Input from "../../common/FormInputs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  createTransfer,
  getBankDetails,
  getAccountDetails,
} from "../../../services/fundraiserService";
import bank from "../../../Assets/Icons/BankIcon.svg";
import Checkbox from "../../common/Checkbox";

const formatBankAccountNumber = (bankData) => {
  if (bankData?.last4) {
    return `XXXX XXXX XXXX ${bankData.last4}`;
  }

  if (bankData?.bankAccount) {
    return `XXXX XXXX XXXX ${bankData.bankAccount}`;
  }

  return "XXXX XXXX XXXX XXXX";
};

const getBankName = (bankData) =>
  bankData?.bank_name || bankData?.bankName || "Bank Account";

const resolveAvailableFundsCents = (accountData, fallbackFundsCents) => {
  const fallbackValue = Number(fallbackFundsCents);
  if (Number.isFinite(fallbackValue) && fallbackValue > 0) {
    return fallbackValue;
  }

  const candidates = [
    accountData?.availableFunds,
    accountData?.availableBalance,
    accountData?.balance,
  ];

  const match = candidates.find((value) => Number.isFinite(Number(value)));
  return Number(match || 0);
};

export default function WithdrawModal({
  isOpen,
  onClose,
  availableFundsCents = 0,
  onWithdrawSuccess,
}) {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [balanceCents, setBalanceCents] = useState(Number(availableFundsCents) || 0);
  const [bankDetails, setBankDetails] = useState(null);
  const [pageContent, setPageContent] = useState("withdraw");

  const getDetails = async () => {
    try {
      const [accountPromise, bankPromise] = await Promise.allSettled([
        dispatch(getAccountDetails())?.unwrap(),
        dispatch(getBankDetails())?.unwrap(),
      ]);

      const accountData =
        accountPromise.status === "fulfilled" && accountPromise.value?.success === true
          ? accountPromise.value?.data
          : null;

      const bankAccounts =
        bankPromise.status === "fulfilled" && bankPromise.value?.success === true
          ? bankPromise.value?.data
          : null;

      setBalanceCents(resolveAvailableFundsCents(accountData, availableFundsCents));
      setBankDetails(
        Array.isArray(bankAccounts) && bankAccounts.length > 0
          ? bankAccounts[0]
          : accountData?.bank || null
      );
    } catch (_) {
      setBalanceCents(Number(availableFundsCents) || 0);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    getDetails();
  }, [isOpen, availableFundsCents]);

  useEffect(() => {
    if (!isOpen) return;
    setPageContent("withdraw");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setBalanceCents(Number(availableFundsCents) || 0);
  }, [availableFundsCents, isOpen]);

  const safeBalanceCents = Number.isFinite(Number(balanceCents))
    ? Number(balanceCents)
    : 0;
  const availableFunds = safeBalanceCents / 100;

  const getValidationSchema = () => {
    return Yup.object().shape({
      amount: Yup.number()
        .required("Amount to withdraw is required")
        .positive("Amount must be positive")
        .max(
          availableFunds,
          Number(availableFunds) > 0
            ? `Insufficient funds. You can only withdraw up to $${availableFunds.toFixed(2)}`
            : "Insufficient funds."
        ),
      bankCheckBox: Yup.boolean().oneOf([true], "You must select a bank"),
    });
  };

  const formik = useFormik({
    initialValues: {
      amount: "",
      bankCheckBox: true,
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      console.log("values", values);
      const createPromise = await dispatch(
        createTransfer({ amount: values?.amount })
      ).unwrap();
      if (createPromise?.success === true) {
        setPageContent("success");
        if (typeof onWithdrawSuccess === "function") {
          await onWithdrawSuccess();
        }
        console.log("success");
      }
    },
    enableReinitialize: true,
  });

  const { amount, bankCheckBox, fundraiserBalance } = formik.values;

  const {
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
  } = formik;

  if (!isOpen) return null;

  const numericAmount = parseFloat(amount || 0);
  const remaining = availableFunds - (Number.isFinite(numericAmount) ? numericAmount : 0);
  const safeRemaining = Number.isFinite(remaining) && remaining > 0 ? remaining : 0;

  const handleAmountKeyDown = (e) => {
    const allowed = ["Backspace","Tab","Enter","Escape","ArrowLeft","ArrowRight","Delete","."];
    if (allowed.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
  };

  const handleAmountChange = (evt) => {
    const raw = evt.target.value;
    const sanitized = raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    setFieldValue("amount", sanitized);
  };

  return (
    <div className="popup-container" onClick={onClose}>
      {pageContent == "withdraw" ? (
        <div className="flex flex-col justify-evenly items-center w-[92vw] max-w-[560px] sm:w-[560px] bg-white rounded-[12px] drop-shadow-[0_3px_3px_rgba(0,0,0,0.25)] popup p-5 sm:p-8" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#000000] font-poppins mb-2">
            Withdraw Funds
          </h2>
          <p className="text-center text-[12px] sm:text-[14px] font-[400] text-[#8C8C8C] font-poppins mb-6 sm:mb-8">
            Enter the amount you want to withdraw
            <span className="block mt-2">
              Available Funds:{" "}
              <span className="text-[#000000] font-[600]">$ {availableFunds.toFixed(2)}</span>
            </span>
          </p>

          <div className="w-full max-w-[480px] withdraw mx-auto">
            <Input
              type="number"
              label="Add Amount"
              placeholder="$ 2000"
              width="100%"
              radius="10px"
              InputDivClass="w-full mb-2"
              name={"amount"}
              value={amount}
              handleChange={handleAmountChange}
              handleBlur={handleBlur}
              error={errors.amount}
              touched={touched.amount}
              onKeyDown={handleAmountKeyDown}
            />
            <p className="text-start text-[12px] sm:text-[14px] font-[400] text-[#8C8C8C] font-poppins mb-4 sm:mb-6">
              Remaining funds:{" "}
              <span className="text-[#000000] font-[600]">
                ${safeRemaining === 0 ? 0 : safeRemaining.toFixed(2)}
              </span>
            </p>
            <div className="flex items-center w-full mb-5 sm:mb-6 h-[64px] sm:h-[70px] rounded-[10px] border-[1px] border-[#00ADEF]">
              <div className="flex justify-start items-center gap-3 sm:gap-5 w-[90%]">
                <img
                  src={bank}
                  className="ml-[5%] h-[22px] sm:h-[25px] md:h-[35px]"
                />
                <div className="">
                  <p className="text-[#303030] my-0 sm:my-1 text-[12px] sm:text-[14px] md:text-[16px] font-poppins font-[600]">
                    {getBankName(bankDetails)}
                  </p>
                  <p className="text-[#8C8C8C] text-[11px] sm:text-[12px] md:text-[14px] font-poppins font-[400]">
                    {formatBankAccountNumber(bankDetails)}
                  </p>
                </div>
              </div>
              <div className="flex justify-end mr-[3%] w-[10%]">
                <Checkbox
                  divClassName="flex items-start"
                  checked={bankCheckBox}
                  onChecked={() => {
                    setFieldValue("bankCheckBox", !bankCheckBox);
                  }}
                  textClass="lg:text-[14px] sm:text-[12px] font-[500] text-[#303030] ml-4"
                />
              </div>
            </div>
            <div className="w-full flex justify-center items-center">
              <div className="flex justify-center items-center gap-3 w-full sm:w-[70%]">
                <PrimaryOutlineButton
                  label={"Cancel"}
                  className={
                    "w-full text-[14px] sm:text-[16px] font-[500] font-poppins px-2 py-2 h-[40px] sm:h-[40px] md:h-[45px] rounded-[10px] whitespace-nowrap"
                  }
                  onClick={onClose}
                />
                <PrimaryButton
                  className={
                    "w-full px-2 py-2 sm:h-[40px] md:h-[45px] rounded-[10px] text-[14px] sm:text-[16px] font-[500] font-poppins"
                  }
                  label={"Withdraw"}
                  onClick={handleSubmit}
                  disabled={!amount || parseFloat(amount) <= 0}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col justify-evenly items-center w-[92vw] max-w-[560px] sm:w-[560px] bg-white rounded-[12px] drop-shadow-[0_3px_3px_rgba(0,0,0,0.25)] popup p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
          <WithdrawSuccess className="h-[100px] w-[100px] mb-6" />
          <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#000000] font-poppins mb-2 text-center">
            Withdrawal Successful!
          </h2>
          <p className="text-center text-[13px] sm:text-[16px] font-[400] text-[#8C8C8C] font-poppins mb-6 sm:mb-8">
            You have successfully withdraw amount {" "}
            <span className="text-[#000000] font-[600]">$ {amount}</span>
          </p>
          <PrimaryButton
            className={
              "w-full sm:w-[50%] px-2 py-2 sm:h-[40px] md:h-[45px] rounded-[10px] text-[14px] sm:text-[16px] font-[500] font-poppins"
            }
            label={"Dismiss"}
            onClick={onClose}
          />
        </div>
      )}
    </div>
  );
}
